import * as React from 'react';

import * as styles from './Gallery.css';
import { GalleryFullscreen } from './components/GalleryFullscreen';
import { GalleryInner } from './components/GalleryInner';
import { InfoControls } from './components/InfoControls';
import { trackFullscreenPhotoSlide, trackPreviewPhotoSlide } from './tracking';
import { TMedia } from './types';

interface IGallerySlots {
  slotFullscreenBefore?: React.ComponentProps<typeof GalleryFullscreen>['slotBefore'];
  slotFullscreenSidebar?: React.ComponentProps<typeof GalleryFullscreen>['slotSidebar'];
  slotScrollerAfter?: React.ReactElement;
}

interface IGalleryProps extends IGallerySlots {
  media: TMedia[];
  onFullscreenOpen?(): void;
  onFullscreenClose?(): void;
}

const GalleryComponent: React.FC<React.PropsWithChildren<IGalleryProps>> = ({
  slotScrollerAfter,
  slotFullscreenBefore,
  slotFullscreenSidebar,
  media,
  onFullscreenOpen,
  onFullscreenClose,
}) => {
  const galleryInnerRef = React.useRef<React.ElementRef<typeof GalleryInner>>(null);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = React.useState(false);
  const previewSlideRef = React.useRef(0);
  const fullscreenSlideRef = React.useRef(0);

  /* istanbul ignore next */
  const handleSlideChange = React.useCallback((index: number) => {
    if (index === previewSlideRef.current) {
      return;
    }

    trackPreviewPhotoSlide(previewSlideRef.current, index);
    previewSlideRef.current = index;
    setCurrentSlide(index);
  }, []);

  /* istanbul ignore next */
  const handleFullscreenSlideChange = React.useCallback((index: number) => {
    if (index === fullscreenSlideRef.current) {
      return;
    }

    trackFullscreenPhotoSlide(fullscreenSlideRef.current, index);
    fullscreenSlideRef.current = index;
    previewSlideRef.current = index;
    galleryInnerRef.current?.scrollTo(index, { behavior: 'auto' });
    setCurrentSlide(index);
  }, []);

  const handleScrollerClick = React.useCallback(() => {
    fullscreenSlideRef.current = previewSlideRef.current;
    setIsFullscreenOpen(true);

    /* istanbul ignore else */
    if (onFullscreenOpen) {
      onFullscreenOpen();
    }
  }, [onFullscreenOpen]);

  const handleFullscreenClose = React.useCallback(() => {
    setIsFullscreenOpen(false);

    /* istanbul ignore else */
    if (onFullscreenClose) {
      onFullscreenClose();
    }
  }, [onFullscreenClose]);

  if (media.length === 0) {
    return <div data-testid="noMedia" className={styles['no-media']} />;
  }

  return (
    <div data-testid="GalleryContainer" className={styles['container']}>
      <GalleryInner
        ref={galleryInnerRef}
        bordered
        fullscreenable
        keyboard={!isFullscreenOpen}
        media={media}
        objectFit="contain"
        withBackgroundBlur
        currentSlide={currentSlide}
        slotScrollerEnd={
          <>
            {slotScrollerAfter}
            <InfoControls currentSlide={currentSlide} media={media} />
          </>
        }
        onSlideChange={handleSlideChange}
        onScrollerClick={handleScrollerClick}
        onFullscreenClick={handleScrollerClick}
      />
      <GalleryFullscreen
        currentSlide={currentSlide}
        slotBefore={slotFullscreenBefore}
        slotSidebar={slotFullscreenSidebar}
        media={media}
        initialSlide={currentSlide}
        open={isFullscreenOpen}
        onSlideChange={handleFullscreenSlideChange}
        onClose={handleFullscreenClose}
      />
    </div>
  );
};

export const Gallery = React.memo(GalleryComponent);
